import { useEffect, useState } from 'react';
import { Participant, RoomEvent } from 'livekit-client';
import { selectRoom } from 'state/selectors';
import { useRaiseHand } from './useRaiseHand';

export const useRaisedHandParticipants = (participants: Participant[]): Participant[] => {
    const { checkIsHandRaisedInMetadata } = useRaiseHand();

    const [raisedIds, setRaisedIds] = useState<ReadonlySet<string>>(
        () => new Set(participants.filter((p) => checkIsHandRaisedInMetadata(p.metadata)).map((p) => p.identity)),
    );

    useEffect(() => {
        const activeIds = new Set(participants.map((p) => p.identity));
        setRaisedIds((prev) => {
            const next = new Set([...prev].filter((id) => activeIds.has(id)));
            participants.forEach((p) => {
                if (checkIsHandRaisedInMetadata(p.metadata)) next.add(p.identity);
            });
            return next;
        });
    }, [participants]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const room = selectRoom();
        if (!room) return;

        const onMetadataChanged = (_: string | undefined, participant: Participant) => {
            const isRaised = Boolean(checkIsHandRaisedInMetadata(participant.metadata));
            setRaisedIds((prev) => {
                const already = prev.has(participant.identity);
                if (isRaised === already) return prev;
                const next = new Set(prev);
                if (isRaised) next.add(participant.identity);
                else next.delete(participant.identity);
                return next;
            });
        };

        room.on(RoomEvent.ParticipantMetadataChanged, onMetadataChanged);
        return () => {
            room.off(RoomEvent.ParticipantMetadataChanged, onMetadataChanged);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return participants.filter((p) => raisedIds.has(p.identity));
};
